import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getNonce, getTables, isPro } from '../Helpers';
import TablesList from './TablesList';
import AdminNotifications from './AdminNotifications';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NoticeModal from './NoticeModal/NoticeModal';
import '../styles/_dashboard.scss';
import Card from '../core/Card';

function Dashboard() {
	const proData = isPro();
	const isProUser = proData;

	const [loading, setLoading] = useState(false);
	const [tables, setTables] = useState([]);
	const [filteredTables, setFilteredTables] = useState([]);
	const [searchKey, setSearchKey] = useState('');
	const fileInputRef = useRef(null);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	const [modalConfig, setModalConfig] = useState({
		isOpen: false,
		type: 'confirmation',
		title: '',
		message: '',
		onConfirm: () => { },
		confirmText: 'Confirm',
		declineText: 'Cancel',
		mode: 'success'
	});

	const closeModal = () => {
		setModalConfig(prev => ({ ...prev, isOpen: false }));
	};

	const showPremiumModal = () => {
		setModalConfig({
			isOpen: true,
			type: 'premium',
			title: '🔥 Premium Feature - Export & Import',
			message: 'Export and import your forms with advanced backup and migration capabilities. Upgrade now to unlock seamless form management!',
			onConfirm: () => {
				window.open('https://functiondeck.com/', '_blank');
				closeModal();
			},
			onClose: closeModal,
			confirmText: 'Upgrade to Pro',
			declineText: 'Maybe Later'
		});
		return;
	};

	/**
	 * Handle import form with premium check
	 */
	const handleImportForm = () => {
		// Check if user has premium access
		if (!isProUser) {
			showPremiumModal();
			return;
		}
		if (fileInputRef.current) {
			// Reset the input value to ensure change event fires even for the same file
			fileInputRef.current.value = '';
			fileInputRef.current.click();
		} else {
			console.error('File input ref is not available');
		}
	};

	/**
	 * Handle export all forms with premium check
	 */
	const handleExportAll = () => {
		// Check if user has premium access
		if (!isProUser) {
			showPremiumModal();
			return;
		}

		if (tables.length === 0) {
			setModalConfig({
				isOpen: true,
				type: 'toast',
				title: 'No Data!',
				message: 'There are no forms to export.',
				position: 'top-right',
				mode: 'error',
			});
			setTimeout(closeModal, 3000);
			return;
		}

		wp.ajax.send('simpleform_export_all_forms', {
			data: {
				nonce: getNonce(),
			},
			success(response) {
				const { export_data, filename } = response;

				// Create and download JSON file
				const dataStr = JSON.stringify(export_data, null, 2);
				const dataBlob = new Blob([dataStr], { type: 'application/json' });
				const url = URL.createObjectURL(dataBlob);

				const link = document.createElement('a');
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Success!',
					message: 'All forms exported successfully.',
					position: 'top-right',
					mode: 'success',
				});
				setTimeout(closeModal, 3000);
			},
			error(error) {
				console.error(error);
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Error!',
					message: 'Failed to export forms.',
					position: 'top-right',
					mode: 'error',
				});
				setTimeout(closeModal, 3000);
			},
		});
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		if (!file) {
			console.log('No file selected');
			return;
		}

		console.log('File selected:', file.name, file.type);

		// Check file type - be more lenient with file type checking
		const isJsonFile = file.type === 'application/json' ||
			file.type === 'text/json' ||
			file.name.toLowerCase().endsWith('.json');

		if (!isJsonFile) {
			setModalConfig({
				isOpen: true,
				type: 'toast',
				title: 'Error!',
				message: 'Please select a valid JSON file.',
				position: 'top-right',
				mode: 'error',
			});
			setTimeout(closeModal, 3000);
			return;
		}

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const importData = e.target.result;
				console.log('File content loaded, length:', importData.length);

				// Handle JSON import
				const parsedData = JSON.parse(importData);
				console.log('JSON parsed successfully:', parsedData);

				// Check if it's a bulk export (contains 'forms' array) or single form
				if (parsedData.forms && Array.isArray(parsedData.forms)) {
					console.log('Detected bulk import with', parsedData.forms.length, 'forms');
					// Handle bulk import
					handleBulkImport(parsedData);
				} else {
					console.log('Detected single form import');
					// Handle single form import
					handleSingleImport(importData);
				}
			} catch (error) {
				console.error('File Parse Error:', error);
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Error!',
					message: 'Invalid JSON file format. Please check your file.',
					position: 'top-right',
					mode: 'error',
				});
				setTimeout(closeModal, 3000);
			}
		};

		reader.onerror = (error) => {
			console.error('FileReader error:', error);
			setModalConfig({
				isOpen: true,
				type: 'toast',
				title: 'Error!',
				message: 'Failed to read the file.',
				position: 'top-right',
				mode: 'error',
			});
			setTimeout(closeModal, 3000);
		};

		reader.readAsText(file);

		// Reset file input
		event.target.value = '';
	};

	/**
	 * Handle bulk import of multiple forms
	 */
	const handleBulkImport = (bulkData) => {
		wp.ajax.send('simpleform_import_bulk_forms', {
			data: {
				nonce: getNonce(),
				import_data: JSON.stringify(bulkData),
			},
			success(response) {
				const importedCount = response.imported_count || 0;
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Success!',
					message: `${importedCount} form(s) imported successfully.`,
					position: 'top-right',
					mode: 'success',
				});
				setTimeout(closeModal, 3000);
				refreshFormsList();
			},
			error(error) {
				console.error('Bulk Import Error:', error);
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Error!',
					message: error.responseJSON?.data?.message || 'Failed to import forms.',
					position: 'top-right',
					mode: 'error',
				});
				setTimeout(closeModal, 3000);
			},
		});
	};

	/**
	 * Handle single form import
	 */
	const handleSingleImport = (importData) => {
		wp.ajax.send('simpleform_import_form', {
			data: {
				nonce: getNonce(),
				import_data: importData,
			},
			success(response) {
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Success!',
					message: 'Form imported successfully.',
					position: 'top-right',
					mode: 'success',
				});
				setTimeout(closeModal, 3000);
				refreshFormsList();
			},
			error(error) {
				console.error('Single Import Error:', error);
				setModalConfig({
					isOpen: true,
					type: 'toast',
					title: 'Error!',
					message: error.responseJSON?.data?.message || 'Failed to import form.',
					position: 'top-right',
					mode: 'error',
				});
				setTimeout(closeModal, 3000);
			},
		});
	};



	/**
	 * Refresh forms list after import
	 */
	const refreshFormsList = () => {
		setLoading(true);
		wp.ajax.send('simpleform_get_tables', {
			data: {
				nonce: getNonce(),
			},
			success(response) {
				setTables(response.tables);
				setFilteredTables(response.tables);
				setLoading(false);
			},
			error(error) {
				console.error('Error fetching tables:', error);
				setLoading(false);
			},
		});
	};

	// Fetch tables data
	useEffect(() => {
		setLoading(true);
		wp.ajax.send('simpleform_get_tables', {
			data: {
				nonce: getNonce(),
			},
			success(response) {
				setTables(response.tables);
				setFilteredTables(response.tables);
				setLoading(false);
			},
			error(error) {
				console.error('Error fetching tables:', error);
				setLoading(false);
			},
		});
	}, []);

	// Filter tables by search key and reset pagination
	useEffect(() => {
		if (searchKey) {
			const filtered = tables.filter(({ form_name }) =>
				form_name.toLowerCase().includes(searchKey.toLowerCase())
			);
			setFilteredTables(filtered);
		} else {
			setFilteredTables(tables);
		}
		setCurrentPage(1); // Reset to first page when search changes
	}, [searchKey, tables]);

	// Calculate pagination
	const totalPages = Math.ceil(filteredTables.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentTables = filteredTables.slice(startIndex, endIndex);

	// Pagination handlers
	const goToPage = (page: number) => {
		setCurrentPage(page);
		// Scroll to top of forms list
		const formsContainer = document.querySelector('.dashboard-content');
		if (formsContainer) {
			formsContainer.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const goToPrevPage = () => {
		if (currentPage > 1) {
			goToPage(currentPage - 1);
		}
	};

	const goToNextPage = () => {
		if (currentPage < totalPages) {
			goToPage(currentPage + 1);
		}
	};

	// Modern Empty state component
	const EmptyState = () => (
		<div className="modern-empty-state">
			<div className="empty-state-content">
				<div className="empty-state-illustration">
					<div className="illustration-bg">
						<div className="form-icon">📝</div>
					</div>
				</div>

				<div className="empty-state-text">
					<h2>Welcome to Form Builder</h2>
					<p>You haven't created any forms yet. Get started by creating your first form or importing an existing one.</p>
				</div>

				<div className="empty-state-actions">
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						accept=".json,application/json"
						style={{ display: 'none' }}
					/>

					<Link to="/preset-form" className="primary-action-btn">
						<AddIcon />
						<span>Create Your First Form</span>
					</Link>

					<button
						onClick={handleImportForm}
						className={`secondary-action-btn ${!isProUser ? 'premium-locked' : ''}`}
						title={isProUser ? "Import Form" : "Import Form (Premium Feature)"}
					>
						<FileUploadIcon />
						<span>Import Existing Form</span>
						{!isProUser && <LockIcon className="lock-icon" />}
					</button>
				</div>

				<div className="empty-state-help">
					<p>
						Need help getting started?
						<a
							href="https://youtu.be/1PnGVuAqIxk"
							target="_blank"
							rel="noreferrer"
							className="help-link"
						>
							Watch our tutorial
						</a>
					</p>
				</div>
			</div>
		</div>
	);

	return (
		<div className="modern-dashboard">
			<AdminNotifications />

			{/* Modern Dashboard Header */}
			<div className="dashboard-header">
				<div className="header-content">
					<div className="header-title">
						<DashboardIcon className="title-icon" />
						<div className="title-text">
							<h1>Form Dashboard</h1>
							<p>Manage and organize your forms</p>
						</div>
					</div>

					<div className="header-stats">
						<div className="stat-card">
							<div className="stat-number">{tables.length}</div>
							<div className="stat-label">Total Forms</div>
						</div>
						{/* <div className="stat-card">
							<div className="stat-number">{filteredTables.length}</div>
							<div className="stat-label">Showing</div>
						</div> */}
					</div>
				</div>
			</div>

			{tables.length === 0 && !loading ? (
				<EmptyState />
			) : (
				<>
					{/* Modern Search and Actions Bar */}
					<div className="dashboard-controls">
						<div className="search-section">
							<div className="search-input-wrapper">
								<SearchIcon className="search-icon" />
								<input
									type="text"
									placeholder="Search forms by name..."
									className="search-input"
									value={searchKey}
									onChange={(e) => setSearchKey(e.target.value.trim())}
								/>
								{searchKey && (
									<button
										className="clear-search"
										onClick={() => setSearchKey('')}
										title="Clear search"
									>
										×
									</button>
								)}
							</div>
							{searchKey && (
								<div className="search-results-info">
									Found {filteredTables.length} of {tables.length} forms
								</div>
							)}
						</div>

						<div className="action-controls">
							<button
								onClick={handleExportAll}
								className={`action-btn export-btn ${!isProUser ? 'premium-locked' : ''}`}
								title={isProUser ? "Export All Forms" : "Export All Forms (Premium Feature)"}
								disabled={tables.length === 0}
							>
								<FileDownloadIcon />
								<span>Export All</span>
								{!isProUser && <LockIcon className="lock-icon" />}
							</button>

							<button
								onClick={handleImportForm}
								className={`action-btn import-btn ${!isProUser ? 'premium-locked' : ''}`}
								title={isProUser ? "Import Form" : "Import Form (Premium Feature)"}
							>
								<FileUploadIcon />
								<span>Import</span>
								{!isProUser && <LockIcon className="lock-icon" />}
							</button>

							<Link to="/create-form" className="action-btn create-btn primary">
								<AddIcon />
								<span>Create Form</span>
							</Link>
						</div>
					</div>

					{/* Hidden file input for import */}
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						accept=".json,application/json"
						style={{ display: 'none' }}
					/>

					{/* Forms Content */}
					<div className="dashboard-content">
						{loading ? (
							<div className="loading-container">
								<div className="loading-spinner"></div>
								<div className="loading-text">Loading your forms...</div>
							</div>
						) : (
							<>
								{filteredTables.length === 0 && searchKey ? (
									<div className="no-results-container">
										<div className="no-results-icon">🔍</div>
										<h3>No forms found</h3>
										<p>No forms match your search criteria for "{searchKey}"</p>
										<button
											className="clear-search-btn"
											onClick={() => setSearchKey('')}
										>
											Clear search and show all forms
										</button>
									</div>
								) : (
									<>
										<div className="forms-list-container">
											{filteredTables.length > 0 && (
												<div className="list-header">
													<div className="results-info">
														Showing {startIndex + 1}-{Math.min(endIndex, filteredTables.length)} of {filteredTables.length} forms
													</div>
													{totalPages > 1 && (
														<div className="pagination-info">
															Page {currentPage} of {totalPages}
														</div>
													)}
												</div>
											)}

											<TablesList
												tables={currentTables}
												setCopiedTables={setFilteredTables}
												setTables={setTables}
												setTableCount={(count: any) => {/* Simplified */ }}
												setLoader={setLoading}
											/>

											{totalPages > 1 && (
												<div className="pagination-container">
													<div className="pagination">
														<button
															className={`pagination-btn prev ${currentPage === 1 ? 'disabled' : ''}`}
															onClick={goToPrevPage}
															disabled={currentPage === 1}
														>
															← Previous
														</button>

														<div className="pagination-numbers">
															{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
																// Show first page, last page, current page, and pages around current
																const showPage =
																	page === 1 ||
																	page === totalPages ||
																	(page >= currentPage - 1 && page <= currentPage + 1);

																if (!showPage) {
																	// Show ellipsis
																	if (page === currentPage - 2 || page === currentPage + 2) {
																		return <span key={page} className="pagination-ellipsis">...</span>;
																	}
																	return null;
																}

																return (
																	<button
																		key={page}
																		className={`pagination-number ${currentPage === page ? 'active' : ''}`}
																		onClick={() => goToPage(page)}
																	>
																		{page}
																	</button>
																);
															})}
														</div>

														<button
															className={`pagination-btn next ${currentPage === totalPages ? 'disabled' : ''}`}
															onClick={goToNextPage}
															disabled={currentPage === totalPages}
														>
															Next →
														</button>
													</div>
												</div>
											)}
										</div>
									</>
								)}
							</>
						)}
					</div>
				</>
			)}

			<NoticeModal
				isOpen={modalConfig.isOpen}
				onClose={closeModal}
				onConfirm={modalConfig.onConfirm}
				onDecline={closeModal}
				type={modalConfig.type}
				mode={modalConfig.mode}
				title={modalConfig.title}
				message={modalConfig.message}
				confirmText={modalConfig.confirmText}
				declineText={modalConfig.declineText}
				position={modalConfig.position || 'center'}
				autoClose={modalConfig.type === 'toast'}
				autoCloseTime={3000}
			/>
		</div>
	);
}

export default Dashboard;